<template>
  <div class="profile-page">
    <div class="section-header">
      <h3>个人信息</h3>
      <el-button v-if="!editing" type="primary" plain size="small" @click="editing = true">
        <i class="el-icon-edit"></i> 编辑信息
      </el-button>
    </div>

    <el-form 
      ref="profileForm" 
      :model="profileForm" 
      :rules="profileRules"
      :disabled="!editing"
      label-width="100px"
      class="profile-form"
    >
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="用户名">
            <el-input v-model="profileForm.username" disabled></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="真实姓名" prop="realname">
            <el-input v-model="profileForm.realname" placeholder="请输入真实姓名"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="手机号" prop="mobile">
            <el-input v-model="profileForm.mobile" placeholder="请输入手机号"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="邮箱" prop="email">
            <el-input v-model="profileForm.email" placeholder="请输入邮箱"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="性别" prop="gender">
            <el-radio-group v-model="profileForm.gender">
              <el-radio :label="1">男</el-radio>
              <el-radio :label="2">女</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="职称" prop="title">
            <el-input v-model="profileForm.title" placeholder="请输入职称"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="单位/机构" prop="organization">
        <el-input v-model="profileForm.organization" placeholder="请输入单位或机构名称"></el-input>
      </el-form-item>

      <el-form-item v-if="editing">
        <el-button type="primary" :loading="saving" @click="saveProfile">保存修改</el-button>
        <el-button @click="cancelEdit">取消</el-button>
      </el-form-item>
    </el-form>

    <!-- 修改密码 -->
    <div class="section-header" style="margin-top: 40px;">
      <h3>修改密码</h3>
    </div>

    <el-form 
      ref="passwordForm" 
      :model="passwordForm" 
      :rules="passwordRules"
      label-width="100px"
      class="password-form"
    >
      <el-form-item label="当前密码" prop="oldPassword">
        <el-input type="password" v-model="passwordForm.oldPassword" placeholder="请输入当前密码" show-password></el-input>
      </el-form-item>
      <el-form-item label="新密码" prop="newPassword">
        <el-input type="password" v-model="passwordForm.newPassword" placeholder="请输入新密码" show-password></el-input>
      </el-form-item>
      <el-form-item label="确认密码" prop="confirmPassword">
        <el-input type="password" v-model="passwordForm.confirmPassword" placeholder="请再次输入新密码" show-password></el-input>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="changingPassword" @click="changePassword">修改密码</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { userApi } from '@/api'
import { getUserInfo, setUserInfo } from '@/utils'

export default {
  name: 'UserProfile',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      editing: false,
      saving: false,
      changingPassword: false,
      profileForm: {
        username: '',
        realname: '',
        mobile: '',
        email: '',
        gender: 1,
        title: '',
        organization: ''
      },
      profileRules: {
        realname: [
          { required: true, message: '请输入真实姓名', trigger: 'blur' }
        ],
        mobile: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ]
      },
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度为6-20个字符', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadUserInfo()
  },
  methods: {
    loadUserInfo() {
      const userInfo = getUserInfo()
      if (userInfo) {
        Object.keys(this.profileForm).forEach(key => {
          if (userInfo[key] !== undefined) {
            this.profileForm[key] = userInfo[key]
          }
        })
      }
    },
    async saveProfile() {
      this.$refs.profileForm.validate(async (valid) => {
        if (!valid) return

        this.saving = true
        try {
          await userApi.updateUserInfo(this.profileForm)
          
          // 更新本地存储
          const userInfo = getUserInfo() || {}
          Object.assign(userInfo, this.profileForm)
          setUserInfo(userInfo)
          
          this.$message.success('保存成功')
          this.editing = false
        } catch (error) {
          console.error('保存失败:', error)
        } finally {
          this.saving = false
        }
      })
    },
    cancelEdit() {
      this.editing = false
      this.loadUserInfo()
    },
    async changePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (!valid) return

        this.changingPassword = true
        try {
          await userApi.changePassword({
            old_password: this.passwordForm.oldPassword,
            new_password: this.passwordForm.newPassword
          })
          
          this.$message.success('密码修改成功')
          this.$refs.passwordForm.resetFields()
        } catch (error) {
          console.error('修改密码失败:', error)
        } finally {
          this.changingPassword = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-page {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;

  h3 {
    font-size: 20px;
    color: #1a237e;
    margin: 0;
  }
}

.profile-form,
.password-form {
  max-width: 700px;
}

@media screen and (max-width: 768px) {
  .profile-form .el-col,
  .password-form .el-col {
    width: 100%;
  }
}
</style>
